import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

import api from "/imports/api/client";

const ICON_TILE_SX = {
  width: "2rem",
  height: "2rem",
  borderRadius: 1,
  bgcolor: "action.hover",
  color: "text.primary",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};
const ITEM_ICON_SX = { minWidth: "2.75rem" };
const ITEM_SX = { py: 1, px: 1.5, borderRadius: 1, mx: 0.75 };
const PRIMARY_PROPS = { fontSize: "0.875rem", fontWeight: 500 };
const SECONDARY_PROPS = { fontSize: "0.75rem" };

function Option({ icon, primary, secondary, ...props }) {
  return (
    <MenuItem sx={ITEM_SX} {...props}>
      <ListItemIcon sx={ITEM_ICON_SX}>
        <Box sx={ICON_TILE_SX}>{icon}</Box>
      </ListItemIcon>
      <ListItemText primary={primary} secondary={secondary} primaryTypographyProps={PRIMARY_PROPS} secondaryTypographyProps={SECONDARY_PROPS} />
    </MenuItem>
  );
}

/**
 * Add to calendar, from a `calendar` payload the server signed:
 * { count, ics, added, google, outlook }.
 *
 * The event's times are real instants the server already converted from the
 * club's clock; nothing here reads a booking date. Google and Outlook web carry
 * one event per link, so a multi-session payload has neither and offers the
 * .ics alone.
 */
function AddToCalendarMenu({ calendar, variant = "button", sx }) {
  const [anchorEl, setAnchorEl] = useState(null);
  if (!calendar?.ics) return null;

  const count = calendar.count || 1;
  const close = () => setAnchorEl(null);

  // Opened synchronously inside the tap so no popup blocker stops it; the
  // server only learns about it afterwards, and a failed report costs nothing.
  const openWeb = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
    api.markCalendarAdded(calendar.added).catch(() => {});
    close();
  };

  const trigger =
    variant === "icon" ? (
      <Tooltip title="Add to calendar">
        <IconButton
          size="small"
          aria-label="Add to calendar"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            width: "1.75rem",
            height: "1.75rem",
            borderRadius: "0.375rem",
            border: "1px solid",
            borderColor: "primary.main",
            color: "primary.main",
            bgcolor: anchorEl ? "action.selected" : "transparent",
            ...sx
          }}
        >
          <EventAvailableIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
    ) : (
      <Button
        variant="outlined"
        fullWidth
        startIcon={<EventAvailableIcon sx={{ fontSize: "1.125rem" }} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ textTransform: "none", ...sx }}
      >
        {count > 1 ? `Add ${count} sessions to calendar` : "Add to calendar"}
      </Button>
    );

  return (
    <>
      {trigger}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ sx: { mt: 0.75, minWidth: "17rem", maxWidth: "20rem", borderRadius: 1.5, boxShadow: "0 8px 28px rgba(26,26,26,0.12)" } }}
        MenuListProps={{ sx: { py: 0.75 } }}
      >
        <Box sx={{ px: 2, pt: 0.75, pb: 1 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>{count > 1 ? `Add ${count} sessions to calendar` : "Add to calendar"}</Typography>
        </Box>
        <Divider sx={{ mb: 0.75 }} />
        {calendar.google && (
          <Option
            icon={<GoogleIcon sx={{ fontSize: "1.125rem" }} />}
            primary="Google Calendar"
            secondary="Opens in a new tab"
            onClick={() => openWeb(calendar.google)}
          />
        )}
        {/* A real link, not a script download: iOS Safari only opens Calendar for
            an HTTP response served as text/calendar. */}
        <Option
          component="a"
          href={api.calendarFileUrl(calendar.ics)}
          onClick={close}
          icon={<AppleIcon sx={{ fontSize: "1.125rem" }} />}
          primary="Apple Calendar"
          secondary="iPhone, Mac or the Outlook app (.ics file)"
        />
        {calendar.outlook && (
          <Option
            icon={<MailOutlineIcon sx={{ fontSize: "1.125rem" }} />}
            primary="Outlook.com"
            secondary="Outlook web or Microsoft 365"
            onClick={() => openWeb(calendar.outlook)}
          />
        )}
        {count > 1 && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", px: 2, pt: 1, pb: 0.5 }}>
            <CalendarMonthOutlinedIcon sx={{ fontSize: "1rem", color: "text.secondary", mt: "0.0625rem" }} />
            <Typography variant="caption" color="text.secondary">
              Using Google Calendar? Add each session from My Bookings.
            </Typography>
          </Box>
        )}
      </Menu>
    </>
  );
}

export default AddToCalendarMenu;
